import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRangePicker } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';
import ReportDatePresets from './ReportDatePresets';
import moment from 'moment';

const DatePicker = ({ onSelect = null, onEmpty = null, date = null }) => {
    const { t, i18n } = useTranslation();
    const [dates, setDates] = useState({
        startDate: date?.startDate || null,
        endDate: date?.endDate || null,
    });
    const [numberOfMonths, setNumberOfMonths] = useState(window.innerWidth < 768 ? 1 : 2); // Initial value based on screen size
    const [focusedInput, setFocusedInput] = useState(null);

    const handleSetDate = ({ startDate, endDate }) => {
        setDates({ startDate, endDate });
        if (startDate && endDate) {
            onSelect?.({ startDate, endDate });
        } else if (!startDate && !endDate) {
            onEmpty?.();
        }
    };

    const handlePresets = ({ start, end }) => {
        setDates({ startDate: start, endDate: end });
        onSelect?.({ startDate: start, endDate: end });
    };

    // Load moment locale dynamically based on the selected language
    useEffect(() => {
        const loadLocale = async (lang) => {
            if (lang === 'en') {
                moment.locale('en');
            } else {
                try {
                    await import(`moment/locale/${lang}`);
                    moment.locale(lang);
                } catch (error) {
                    console.error('Locale data not found:', error);
                }
            }
        };
        loadLocale(i18n.language.toLowerCase());
    }, [i18n.language]);

    // Update dates if props change
    useEffect(() => {
        if (date?.startDate?.isValid() && date?.endDate?.isValid()) {
            setDates({
                startDate: date.startDate,
                endDate: date.endDate,
            });
        }
    }, [date]);

    // Adjust number of months based on screen size
    useEffect(() => {
        const updateNumberOfMonths = () => {
            setNumberOfMonths(window.innerWidth < 768 ? 1 : 2);
        };
        window.addEventListener('resize', updateNumberOfMonths);
        return () => window.removeEventListener('resize', updateNumberOfMonths);
    }, []);

    return (
        <DateRangePicker
            startDate={dates.startDate}
            endDate={dates.endDate}
            onDatesChange={handleSetDate}
            focusedInput={focusedInput}
            onFocusChange={setFocusedInput}
            startDateId="startDate"
            endDateId="endDate"
            startDatePlaceholderText={t("admin.common.from")}
            endDatePlaceholderText={t("admin.common.to")}
            numberOfMonths={numberOfMonths}
            isOutsideRange={() => false}
            showClearDates={true}
            hideKeyboardShortcutsPanel={true}
            minimumNights={0}
            keepOpenOnDateSelect={false}
            renderCalendarInfo={() => <ReportDatePresets handlePresets={handlePresets} />}
            initialVisibleMonth={() => dates.startDate || moment()}
        />
    );
};

export default DatePicker;
